import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, Building, Search, CheckCircle, XCircle, Filter, 
  Mail, Phone, MapPin, Award, Calendar, DollarSign, 
  FileText, AlertCircle, Globe, Star, Shield, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminPartnersPage({ user }) {
  const [activeTab, setActiveTab] = useState('coaches'); // coaches or facilities
  const [coachApplications, setCoachApplications] = useState([]);
  const [facilityApplications, setFacilityApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, approved, rejected
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  useEffect(() => {
    fetchApplications();
  }, []);
  
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/synergies/admin/applications`, {
        withCredentials: true
      });
      setCoachApplications(response.data.coaches || []);
      setFacilityApplications(response.data.facilities || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load applications');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleApproveCoach = async (applicationId) => {
    try {
      await axios.put(
        `${API}/synergies/admin/applications/coach/${applicationId}/approve`,
        { admin_notes: 'Approved by admin' },
        { withCredentials: true }
      );
      toast.success('Coach application approved! Live profile created.');
      fetchApplications();
      setSelectedItems([]);
    } catch (error) {
      console.error('Error approving coach:', error);
      toast.error('Failed to approve coach application');
    }
  };
  
  const handleRejectCoach = async (applicationId, reason = '') => {
    try {
      await axios.put(
        `${API}/synergies/admin/applications/coach/${applicationId}/reject`,
        { admin_notes: reason || 'Application does not meet requirements' },
        { withCredentials: true }
      );
      toast.success('Coach application rejected');
      fetchApplications();
      setSelectedItems([]);
    } catch (error) {
      console.error('Error rejecting coach:', error);
      toast.error('Failed to reject coach application');
    }
  };
  
  const handleApproveFacility = async (applicationId) => {
    try {
      await axios.put(
        `${API}/synergies/admin/applications/facility/${applicationId}/approve`,
        { admin_notes: 'Approved by admin' },
        { withCredentials: true }
      );
      toast.success('Facility application approved! Live facility created.');
      fetchApplications();
      setSelectedItems([]);
    } catch (error) {
      console.error('Error approving facility:', error);
      toast.error('Failed to approve facility application');
    }
  };
  
  const handleRejectFacility = async (applicationId, reason = '') => {
    try {
      await axios.put(
        `${API}/synergies/admin/applications/facility/${applicationId}/reject`,
        { admin_notes: reason || 'Application does not meet requirements' },
        { withCredentials: true }
      );
      toast.success('Facility application rejected');
      fetchApplications();
      setSelectedItems([]);
    } catch (error) {
      console.error('Error rejecting facility:', error);
      toast.error('Failed to reject facility application');
    }
  };
  
  const handleBulkApprove = async () => {
    if (selectedItems.length === 0) {
      toast.error('No items selected');
      return;
    }
    
    setBulkActionLoading(true);
    try {
      const promises = selectedItems.map(id => {
        if (activeTab === 'coaches') {
          return handleApproveCoach(id);
        } else {
          return handleApproveFacility(id);
        }
      });
      await Promise.all(promises);
      toast.success(`${selectedItems.length} applications approved`);
      setSelectedItems([]);
    } catch (error) {
      toast.error('Some approvals failed');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  const handleBulkReject = async () => {
    if (selectedItems.length === 0) {
      toast.error('No items selected');
      return;
    }
    
    const reason = prompt('Enter rejection reason (optional):');
    
    setBulkActionLoading(true);
    try {
      const promises = selectedItems.map(id => {
        if (activeTab === 'coaches') {
          return handleRejectCoach(id, reason);
        } else {
          return handleRejectFacility(id, reason);
        }
      });
      await Promise.all(promises);
      toast.success(`${selectedItems.length} applications rejected`);
      setSelectedItems([]);
    } catch (error) {
      toast.error('Some rejections failed');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  const toggleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };
  
  const toggleSelectAll = () => {
    const currentList = activeTab === 'coaches' ? filteredCoaches : filteredFacilities;
    if (selectedItems.length === currentList.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(currentList.map(item => item.application_id));
    }
  };
  
  // Filter applications
  const filteredCoaches = coachApplications.filter(app => {
    const matchesSearch = 
      app.applicant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.applicant_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.country?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  const filteredFacilities = facilityApplications.filter(app => {
    const matchesSearch = 
      app.facility_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.applicant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.applicant_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.country?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  const currentApplications = activeTab === 'coaches' ? filteredCoaches : filteredFacilities;
  const pendingCount = currentApplications.filter(app => app.status === 'pending').length;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-deep-dark py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
            <h1 
              className="text-4xl md:text-5xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Partner <span className="gold-gradient">Applications</span>
            </h1>
          </div>
          <p className="text-white/70 text-lg">
            Review and manage DIOS Synergies partner applications
          </p>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-premium-gold/30/50">
          <button
            onClick={() => { setActiveTab('coaches'); setSelectedItems([]); }}
            className={`px-6 py-3 font-bold tracking-wider transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'coaches'
                ? 'text-premium-gold border-b-2 border-energy-red'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <Award className="w-5 h-5" strokeWidth={1.5} />
            Coach Applications
            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
              activeTab === 'coaches' ? 'bg-premium-gold text-white' : 'bg-dark-surface text-white/60'
            }`}>
              {coachApplications.filter(app => app.status === 'pending').length}
            </span>
          </button>
          <button
            onClick={() => { setActiveTab('facilities'); setSelectedItems([]); }}
            className={`px-6 py-3 font-bold tracking-wider transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'facilities'
                ? 'text-premium-gold border-b-2 border-premium-gold'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <Building className="w-5 h-5" />
            Facility Applications
            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
              activeTab === 'facilities' ? 'bg-premium-gold text-dark-purple' : 'bg-dark-surface text-white/60'
            }`}>
              {facilityApplications.filter(app => app.status === 'pending').length}
            </span>
          </button>
        </div>
        
        {/* Filters and Search */}
        <div className="bg-dark-surface rounded-xl p-6 mb-6 border border-premium-gold/30/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  placeholder="Search by name, email, location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-deep-dark border border-premium-gold/30/50 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-premium-gold transition-colors"
                />
              </div>
            </div>
            
            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30/50 rounded-lg text-white focus:outline-none focus:border-premium-gold transition-colors"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
          
          {/* Bulk Actions */}
          {selectedItems.length > 0 && (
            <div className="mt-4 pt-4 border-t border-premium-gold/30/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-white/70">
                  {selectedItems.length} selected
                </span>
                <button
                  onClick={toggleSelectAll}
                  className="text-premium-gold hover:text-premium-gold/80 text-sm font-semibold uppercase"
                >
                  {selectedItems.length === currentApplications.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleBulkApprove}
                  disabled={bulkActionLoading}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold tracking-wider px-6 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Bulk Approve
                </button>
                <button
                  onClick={handleBulkReject}
                  disabled={bulkActionLoading}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold tracking-wider px-6 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Bulk Reject
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-dark-surface rounded-xl p-4 border border-premium-gold/30/50">
            <p className="text-white/60 text-sm font-semibold mb-1">Total</p>
            <p className="text-3xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
              {currentApplications.length}
            </p>
          </div>
          <div className="bg-dark-surface rounded-xl p-4 border border-orange-500/30">
            <p className="text-white/60 text-sm font-semibold mb-1">Pending</p>
            <p className="text-3xl font-black text-orange-500" style={{ fontFamily: 'Fraunces, serif' }}>
              {currentApplications.filter(app => app.status === 'pending').length}
            </p>
          </div>
          <div className="bg-dark-surface rounded-xl p-4 border border-green-500/30">
            <p className="text-white/60 text-sm font-semibold mb-1">Approved</p>
            <p className="text-3xl font-black text-green-500" style={{ fontFamily: 'Fraunces, serif' }}>
              {currentApplications.filter(app => app.status === 'approved').length}
            </p>
          </div>
          <div className="bg-dark-surface rounded-xl p-4 border border-red-500/30">
            <p className="text-white/60 text-sm font-semibold mb-1">Rejected</p>
            <p className="text-3xl font-black text-red-500" style={{ fontFamily: 'Fraunces, serif' }}>
              {currentApplications.filter(app => app.status === 'rejected').length}
            </p>
          </div>
        </div>
        
        {/* Applications List */}
        <div className="space-y-4">
          {currentApplications.length === 0 ? (
            <div className="bg-dark-surface rounded-xl p-12 text-center border border-premium-gold/30/50">
              <AlertCircle className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/60 text-lg">No applications found</p>
            </div>
          ) : (
            currentApplications.map((app) => (
              <ApplicationCard
                key={app.application_id}
                application={app}
                type={activeTab}
                isSelected={selectedItems.includes(app.application_id)}
                onToggleSelect={toggleSelectItem}
                onApprove={activeTab === 'coaches' ? handleApproveCoach : handleApproveFacility}
                onReject={activeTab === 'coaches' ? handleRejectCoach : handleRejectFacility}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Application Card Component
function ApplicationCard({ application, type, isSelected, onToggleSelect, onApprove, onReject }) {
  const [showDetails, setShowDetails] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-orange-500/20 text-orange-500 border-orange-500/50',
      approved: 'bg-green-500/20 text-green-500 border-green-500/50',
      rejected: 'bg-red-500/20 text-red-500 border-red-500/50'
    };
    return styles[status] || styles.pending;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const handleRejectClick = () => {
    if (application.status === 'pending') {
      setShowRejectModal(true);
    }
  };
  
  const confirmReject = () => {
    onReject(application.application_id, rejectReason);
    setShowRejectModal(false);
    setRejectReason('');
  };
  
  return (
    <div className={`bg-dark-surface rounded-xl border transition-all duration-200 ${
      isSelected ? 'border-premium-gold shadow-lg' : 'border-premium-gold/30/50'
    }`}>
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-4 flex-1">
            {/* Checkbox */}
            {application.status === 'pending' && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelect(application.application_id)}
                className="mt-1 w-5 h-5 rounded border-2 border-premium-gold/30 bg-deep-dark checked:bg-premium-gold checked:border-premium-gold focus:outline-none cursor-pointer"
              />
            )}
            
            <div className="flex-1">
              {/* Title and Status */}
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {type === 'coaches' ? application.applicant_name : application.facility_name}
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusBadge(application.status)}`}>
                  {application.status}
                </span>
              </div>
              
              {/* Key Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center gap-2 text-white/70">
                  <Users className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">{application.applicant_name}</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">{application.applicant_email}</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">{application.applicant_phone}</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">{application.city}, {application.country}</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <Calendar className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">Applied: {formatDate(application.created_at)}</span>
                </div>
                {type === 'coaches' && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Clock className="w-4 h-4" strokeWidth={1.5} />
                    <span className="text-sm">{application.experience_years} years exp</span>
                  </div>
                )}
              </div>
              
              {/* Quick Details */}
              {type === 'coaches' ? (
                <div className="flex flex-wrap gap-2 mb-3">
                  {application.specializations?.slice(0, 3).map((sport, idx) => (
                    <span key={idx} className="px-3 py-1 bg-premium-gold/20 text-premium-gold text-xs rounded-full font-semibold uppercase">
                      {sport}
                    </span>
                  ))}
                  {application.specializations?.length > 3 && (
                    <span className="px-3 py-1 bg-dark-surface/50 text-white/60 text-xs rounded-full">
                      +{application.specializations.length - 3} more
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-3 py-1 bg-premium-gold/20 text-premium-gold text-xs rounded-full font-semibold uppercase">
                    {application.facility_type?.replace(/_/g, ' ')}
                  </span>
                  {application.available_sports?.slice(0, 2).map((sport, idx) => (
                    <span key={idx} className="px-3 py-1 bg-premium-gold/20 text-premium-gold text-xs rounded-full font-semibold uppercase">
                      {sport}
                    </span>
                  ))}
                  {application.available_sports?.length > 2 && (
                    <span className="px-3 py-1 bg-dark-surface/50 text-white/60 text-xs rounded-full">
                      +{application.available_sports.length - 2} more sports
                    </span>
                  )}
                </div>
              )}
              
              {/* Pricing */}
              {type === 'coaches' && application.hourly_rate && (
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-premium-gold" strokeWidth={1.5} />
                  <span className="text-white font-bold">₹{application.hourly_rate}/hour</span>
                </div>
              )}
              
              {/* Admin Notes */}
              {application.admin_notes && (
                <div className="mt-3 p-3 bg-deep-dark rounded-lg border border-premium-gold/30/50">
                  <p className="text-xs text-white/60 font-semibold mb-1">Admin Notes:</p>
                  <p className="text-sm text-white/80">{application.admin_notes}</p>
                </div>
              )}
              
              {/* Details Toggle */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="mt-3 text-premium-gold hover:text-premium-gold/80 text-sm font-semibold flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                {showDetails ? 'Hide Details' : 'View Full Details'}
              </button>
              
              {/* Extended Details */}
              {showDetails && (
                <div className="mt-4 p-4 bg-deep-dark rounded-lg border border-premium-gold/30/50 space-y-3">
                  {type === 'coaches' ? (
                    <>
                      <div>
                        <p className="text-xs text-white/60 font-semibold mb-1">Bio:</p>
                        <p className="text-sm text-white/80">{application.bio || 'No bio provided'}</p>
                      </div>
                      {application.certifications && application.certifications.length > 0 && (
                        <div>
                          <p className="text-xs text-white/60 font-semibold mb-2">Certifications:</p>
                          <div className="flex flex-wrap gap-2">
                            {application.certifications.map((cert, idx) => (
                              <span key={idx} className="px-2 py-1 bg-premium-gold/20 text-premium-gold text-xs rounded">
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-xs text-white/60 font-semibold mb-1">Description:</p>
                        <p className="text-sm text-white/80">{application.description || 'No description provided'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60 font-semibold mb-1">Address:</p>
                        <p className="text-sm text-white/80">{application.address}</p>
                      </div>
                      {application.pricing_info && Object.keys(application.pricing_info).length > 0 && (
                        <div>
                          <p className="text-xs text-white/60 font-semibold mb-2">Pricing:</p>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(application.pricing_info).map(([key, value]) => (
                              <div key={key} className="flex justify-between text-sm">
                                <span className="text-white/60">{key.replace(/_/g, ' ')}:</span>
                                <span className="text-white font-semibold">₹{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        {application.status === 'pending' && (
          <div className="flex gap-3 mt-4 pt-4 border-t border-premium-gold/30/50">
            <button
              onClick={() => onApprove(application.application_id)}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold tracking-wider px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Approve & Create Profile
            </button>
            <button
              onClick={handleRejectClick}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold tracking-wider px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              <XCircle className="w-5 h-5" />
              Reject Application
            </button>
          </div>
        )}
        
        {application.status === 'approved' && application.reviewed_at && (
          <div className="mt-4 pt-4 border-t border-premium-gold/30/50 flex items-center gap-2 text-green-500">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm">Approved on {formatDate(application.reviewed_at)}</span>
          </div>
        )}
        
        {application.status === 'rejected' && application.reviewed_at && (
          <div className="mt-4 pt-4 border-t border-premium-gold/30/50 flex items-center gap-2 text-red-500">
            <XCircle className="w-5 h-5" />
            <span className="text-sm">Rejected on {formatDate(application.reviewed_at)}</span>
          </div>
        )}
      </div>
      
      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-surface rounded-xl p-6 max-w-md w-full border border-red-500/50">
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Reject Application
            </h3>
            <p className="text-white/70 mb-4">
              Please provide a reason for rejection (optional):
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              rows={4}
              className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30/50 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-red-500 transition-colors mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 bg-deep-dark hover:bg-deep-dark text-white font-bold tracking-wider px-6 py-3 rounded-lg transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold tracking-wider px-6 py-3 rounded-lg transition-all duration-300"
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPartnersPage;
